/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { getCancellationReasons } from '../../services/routing/routingApi';

// =============================================================================
// CancellationReasonPicker
//
// Dropdown do MOTIVO de exclusão (cancelamento) de item de rota — handoff 0048.
// O DELETE do item passou a exigir cancellation_reason_id; este picker oferece o
// catálogo (seed) e devolve o id escolhido. Espelha o NonCompletionReasonPicker
// (mesma mecânica de fetch + cache module-level) mas consome outro endpoint.
//
// Faz fetch interno via getCancellationReasons (GET /routing/cancellation-reasons
// ?active=1). Mantém cache module-level pra evitar refetch quando o dialog reabre
// na mesma navegação. O cache é resetado em reload de página.
//
// Uso:
//   <CancellationReasonPicker
//      value={reasonId}
//      onChange={setReasonId}
//      labelText="Motivo da exclusão"
//      required
//   />
// =============================================================================

let cachedReasons = null;
let inFlightPromise = null;

function fetchReasonsCached() {
   if (cachedReasons) return Promise.resolve(cachedReasons);
   if (inFlightPromise) return inFlightPromise;
   inFlightPromise = getCancellationReasons({ active: true })
      .then((list) => {
         cachedReasons = Array.isArray(list) ? list : [];
         return cachedReasons;
      })
      .finally(() => {
         inFlightPromise = null;
      });
   return inFlightPromise;
}

const Wrap = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
`;

const Label = styled.label`
   font-size: 0.75rem;
   font-weight: 600;
   color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};
`;

const Select = styled.select`
   padding: 8px 10px;
   font-size: 0.85rem;
   border-radius: 8px;
   border: 1px solid
      ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.15)'};
`;

const EmptyHint = styled.span`
   font-size: 0.7rem;
   color: #c62828;
   margin-top: 4px;
`;

export default function CancellationReasonPicker({
   value = '',
   onChange,
   disabled = false,
   required = false,
   id = undefined,
   labelText = undefined,
}) {
   const [reasons, setReasons] = useState(cachedReasons || []);
   const [isLoading, setIsLoading] = useState(!cachedReasons);

   useEffect(() => {
      // Cache hit: já temos a lista; render direto.
      if (cachedReasons) {
         setReasons(cachedReasons);
         setIsLoading(false);
         return undefined;
      }

      let cancelled = false;
      setIsLoading(true);

      fetchReasonsCached()
         .then((list) => {
            if (cancelled) return;
            setReasons(list);
         })
         .catch(() => {
            if (cancelled) return;
            // Falha silenciosa no fetch do catálogo — sem motivos, o dialog
            // consumidor mantém o botão de excluir desabilitado (não há o que
            // selecionar).
            setReasons([]);
         })
         .finally(() => {
            if (!cancelled) setIsLoading(false);
         });

      return () => {
         cancelled = true;
      };
   }, []);

   return (
      <Wrap>
         {labelText && (
            <Label htmlFor={id}>
               {labelText}
               {required ? ' *' : ''}
            </Label>
         )}
         <Select
            id={id}
            value={value ?? ''}
            onChange={(event) => onChange(event.target.value)}
            disabled={disabled || isLoading}
         >
            <option value="">
               {isLoading ? 'Carregando…' : '— selecione —'}
            </option>
            {reasons.map((reason) => (
               <option key={reason.id} value={reason.id}>
                  {reason.description}
               </option>
            ))}
         </Select>
         {!isLoading && reasons.length === 0 && (
            <EmptyHint>
               Nenhum motivo de cancelamento disponível no momento.
            </EmptyHint>
         )}
      </Wrap>
   );
}

CancellationReasonPicker.propTypes = {
   value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   onChange: PropTypes.func.isRequired,
   disabled: PropTypes.bool,
   required: PropTypes.bool,
   id: PropTypes.string,
   labelText: PropTypes.string,
};
